import { toQueryString, type Paginated } from '@relay-towing/api';
import env from '../config/env';

export interface GetListOptions {
  /**
   * The bearer token. Every list hook already holds one from
   * `getAccessTokenSilently`, so it is passed in rather than looked up here.
   * Leave it out and no Authorization header is sent, as driverApi does when
   * its TokenManager has nothing.
   */
  token?: string | null;
  /**
   * Sent as `x-company-id`. Every company list needs it: the backend
   * refuses a company route that names no company, so pass the selected
   * one. `/drivers` was once called without it, on the belief that it then
   * answered for every company the user belongs to; it answers 403.
   */
  companyId?: string | null;
}

/** A failed list request. `status` is what `shouldRetryQuery` and
 *  `authErrorKindFromError` read to tell a 401 from a 500. */
export type ListRequestError = Error & { status: number };

/**
 * A list endpoint's path with its query string, for callers that must keep
 * their own HTTP client: driverApi's and lib/api's axios instances carry
 * the token refresh, stored company selection, timeout and 401 redirect that
 * the impounds, tasks and invoices lists depend on. Type their response as
 * `Paginated<Item>` from @relay-towing/api.
 */
export function listPath(url: string, params: object = {}): string {
  return `${url}${toQueryString(params)}`;
}

/**
 * One page of a list endpoint, as `Paginated<Item>`.
 *
 * Every paginated list request in the driver app goes through here, so the
 * query string is built one way and the response is read one way. Pass the
 * endpoint's `ListParams<SortBy>` and filters as `params`, and a path under
 * the API root as `url`.
 *
 * Built on fetch because most of the list hooks already were, each with its
 * own token and `x-company-id`. driverApi's axios client would have put its
 * ten-second timeout on the 500-call and 1000-driver pages.
 */
export async function getList<Item>(
  url: string,
  params: object = {},
  { token, companyId }: GetListOptions = {}
): Promise<Paginated<Item>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (companyId) headers['x-company-id'] = companyId;

  const response = await fetch(`${env.apiUrl}${listPath(url, params)}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = new Error(
      `Failed to fetch ${url}: ${response.statusText}`
    ) as ListRequestError;
    error.status = response.status;
    throw error;
  }

  return (await response.json()) as Paginated<Item>;
}
